import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Edit, Save, Undo, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { TradingPatternAnalysisService, type TradingFile } from '@/services/TradingPatternAnalysisService';

interface TradeEdit {
  position: number;
  originalValue: number;
  newValue: number;
}

interface TradeValueEditorProps {
  onRefresh?: () => void;
}

export const TradeValueEditor: React.FC<TradeValueEditorProps> = ({ onRefresh }) => {
  const [tradingFiles, setTradingFiles] = useState<TradingFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<TradingFile | null>(null);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Edit state
  const [editPosition, setEditPosition] = useState<string>('');
  const [editValue, setEditValue] = useState<string>('');
  const [pendingEdits, setPendingEdits] = useState<TradeEdit[]>([]);
  
  // Preview state
  const [currentValue, setCurrentValue] = useState<number | null>(null);
  const [isValidPosition, setIsValidPosition] = useState(false);

  useEffect(() => {
    loadTradingFiles();
  }, []);

  useEffect(() => {
    validatePosition();
  }, [editPosition, selectedFile]);

  const loadTradingFiles = async () => {
    setIsLoadingFiles(true);
    try {
      const result = await TradingPatternAnalysisService.getTradingFiles();
      if (result.success && result.data) {
        setTradingFiles(result.data);
      } else {
        toast.error(result.error || 'Failed to load trading files');
      }
    } catch (error) {
      console.error('Error loading trading files:', error);
      toast.error('Failed to load trading files');
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const validatePosition = async () => {
    if (!selectedFile || !editPosition.trim()) {
      setIsValidPosition(false);
      setCurrentValue(null);
      return;
    }

    const position = parseInt(editPosition.trim());
    if (isNaN(position) || position < 1) {
      setIsValidPosition(false);
      setCurrentValue(null);
      return;
    }

    // Get current trading data to validate position
    try {
      const { data, error } = await supabase
        .from('trading_files')
        .select('trading_data')
        .eq('id', selectedFile.id)
        .single();

      if (error) {
        console.error('Error fetching trading data:', error);
        setIsValidPosition(false);
        setCurrentValue(null);
        return;
      }

      const tradingData = data.trading_data as number[];
      if (position > tradingData.length) {
        setIsValidPosition(false);
        setCurrentValue(null);
        return;
      }

      // Position is 1-based, array is 0-based
      const arrayIndex = position - 1;
      setCurrentValue(tradingData[arrayIndex]);
      setIsValidPosition(true);
    } catch (error) {
      console.error('Error validating position:', error);
      setIsValidPosition(false);
      setCurrentValue(null);
    }
  };

  const addEdit = () => {
    if (!selectedFile || !isValidPosition || currentValue === null) {
      toast.error('Please select a valid file and position');
      return;
    }

    const position = parseInt(editPosition.trim());
    const newValue = parseInt(editValue.trim());

    if (isNaN(newValue) || (newValue !== 0 && newValue !== 1)) {
      toast.error('Value must be 0 or 1');
      return;
    }

    if (newValue === currentValue) {
      toast.warning('New value is the same as current value');
      return;
    }

    // Check if position already has a pending edit
    const existingEditIndex = pendingEdits.findIndex(edit => edit.position === position);
    
    if (existingEditIndex >= 0) {
      // Update existing edit
      const updatedEdits = [...pendingEdits];
      updatedEdits[existingEditIndex] = {
        position,
        originalValue: updatedEdits[existingEditIndex].originalValue,
        newValue
      };
      setPendingEdits(updatedEdits);
      toast.success(`Updated edit for position ${position}`);
    } else {
      // Add new edit
      const newEdit: TradeEdit = {
        position,
        originalValue: currentValue,
        newValue
      };
      setPendingEdits(prev => [...prev, newEdit].sort((a, b) => a.position - b.position));
      toast.success(`Added edit for position ${position}`);
    }

    // Clear inputs
    setEditPosition('');
    setEditValue('');
    setCurrentValue(null);
    setIsValidPosition(false);
  };

  const removeEdit = (position: number) => {
    setPendingEdits(prev => prev.filter(edit => edit.position !== position));
    toast.success(`Removed edit for position ${position}`);
  };

  const applyEdits = async () => {
    if (!selectedFile || pendingEdits.length === 0) {
      toast.error('No edits to apply');
      return;
    }

    setIsUpdating(true);
    try {
      // Get current trading data
      const { data, error } = await supabase
        .from('trading_files')
        .select('trading_data')
        .eq('id', selectedFile.id)
        .single();

      if (error) {
        throw new Error('Failed to fetch current trading data');
      }

      const tradingData = [...(data.trading_data as number[])];
      
      // Apply all edits
      for (const edit of pendingEdits) {
        const arrayIndex = edit.position - 1; // Convert to 0-based index
        tradingData[arrayIndex] = edit.newValue;
      }

      // Recalculate statistics
      const winTrades = tradingData.filter(x => x === 1).length;
      const lossTrades = tradingData.filter(x => x === 0).length;
      const winRate = tradingData.length > 0 ? Math.round((winTrades / tradingData.length) * 100 * 100) / 100 : 0;

      // Update database
      const { error: updateError } = await supabase
        .from('trading_files')
        .update({
          trading_data: tradingData,
          win_trades: winTrades,
          loss_trades: lossTrades,
          win_rate: winRate,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedFile.id);

      if (updateError) {
        throw new Error('Failed to update trading data');
      }

      // Log the changes
      console.log(`[TRADE-EDIT] Applied ${pendingEdits.length} edits to file "${selectedFile.filename}"`);
      console.log('[TRADE-EDIT] Edits:', pendingEdits);
      console.log(`[TRADE-EDIT] New stats - Win: ${winTrades}, Loss: ${lossTrades}, Win Rate: ${winRate}%`);

      toast.success(`Successfully applied ${pendingEdits.length} edits`);
      
      // Clear pending edits
      setPendingEdits([]);
      
      // Refresh file data
      await loadTradingFiles();
      
      // Update selected file data
      setSelectedFile(prev => prev ? {
        ...prev,
        win_trades: winTrades,
        loss_trades: lossTrades,
        win_rate: winRate
      } : null);

      // Trigger parent refresh if provided
      onRefresh?.();

    } catch (error) {
      console.error('Error applying edits:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to apply edits');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Trade Value Editor
          </CardTitle>
          <CardDescription>
            Edit individual trade values in trading data arrays. Use 1-based indexing (position 1 = first trade).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* File Selection */}
          <div className="space-y-2">
            <Label htmlFor="file-select">Select Trading File</Label>
            <Select
              value={selectedFile?.id || ''}
              onValueChange={(value) => {
                const file = tradingFiles.find(f => f.id === value);
                setSelectedFile(file || null);
                setPendingEdits([]); // Clear edits when changing files
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose a trading file..." />
              </SelectTrigger>
              <SelectContent>
                {tradingFiles.map((file) => (
                  <SelectItem key={file.id} value={file.id}>
                    <div className="flex items-center justify-between w-full">
                      <span>{file.filename}</span>
                      <Badge variant="secondary" className="ml-2">
                        {file.total_trades} trades
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedFile && (
            <>
              {/* File Info */}
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <h3 className="font-medium">{selectedFile.filename}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Total Trades: </span>
                    <span className="font-medium">{selectedFile.total_trades}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Win Trades: </span>
                    <span className="font-medium text-green-600">{selectedFile.win_trades}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Loss Trades: </span>
                    <span className="font-medium text-red-600">{selectedFile.loss_trades}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Win Rate: </span>
                    <span className="font-medium">{selectedFile.win_rate}%</span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Edit Form */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="position">Position (1-based)</Label>
                  <Input
                    id="position"
                    value={editPosition}
                    onChange={(e) => setEditPosition(e.target.value)}
                    placeholder="e.g., 3482"
                    type="number"
                    min="1"
                    max={selectedFile.total_trades}
                  />
                  {editPosition && !isValidPosition && (
                    <p className="text-sm text-red-600">Invalid position</p>
                  )}
                  {isValidPosition && currentValue !== null && (
                    <p className="text-sm text-green-600">Current value: {currentValue}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="value">New Value</Label>
                  <Select value={editValue} onValueChange={setEditValue}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose value..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0 (Loss)</SelectItem>
                      <SelectItem value="1">1 (Win)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button
                    onClick={addEdit}
                    disabled={!isValidPosition || !editValue || currentValue === null}
                    className="w-full"
                  >
                    Add Edit
                  </Button>
                </div>
              </div>

              {/* Pending Edits */}
              {pendingEdits.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium">Pending Edits ({pendingEdits.length})</h3>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPendingEdits([])}
                        >
                          <Undo className="h-4 w-4 mr-2" />
                          Clear All
                        </Button>
                        <Button
                          onClick={applyEdits}
                          disabled={isUpdating}
                          size="sm"
                        >
                          <Save className="h-4 w-4 mr-2" />
                          {isUpdating ? 'Applying...' : 'Apply Edits'}
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {pendingEdits.map((edit) => (
                        <div
                          key={edit.position}
                          className="flex items-center justify-between p-3 bg-muted rounded-lg"
                        >
                          <div className="flex items-center gap-4">
                            <span className="font-medium">Position {edit.position}</span>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{edit.originalValue}</Badge>
                              <span>→</span>
                              <Badge variant={edit.newValue === 1 ? 'default' : 'secondary'}>
                                {edit.newValue}
                              </Badge>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeEdit(edit.position)}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Warning */}
              <div className="flex items-start gap-2 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-800 dark:text-yellow-200">Warning</p>
                  <p className="text-yellow-700 dark:text-yellow-300">
                    Editing trade values will recalculate all statistics and may affect existing pattern analysis.
                    This action cannot be undone.
                  </p>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};